import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Package, ShoppingCart, DollarSign, TrendingUp, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminShop = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/shop/stats`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setStats(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const cards = [
    { label: 'Active Products', value: stats?.total_products ?? 0, icon: Package, color: 'bg-blue-100 text-blue-600', link: '/admin/shop/products' },
    { label: 'Total Orders', value: stats?.total_orders ?? 0, icon: ShoppingCart, color: 'bg-purple-100 text-purple-600', link: '/admin/shop/orders' },
    { label: 'Paid Orders', value: stats?.paid_orders ?? 0, icon: TrendingUp, color: 'bg-green-100 text-green-600', link: '/admin/shop/orders' },
    { label: 'Pending Orders', value: stats?.pending_orders ?? 0, icon: Clock, color: 'bg-amber-100 text-amber-600', link: '/admin/shop/orders' },
    { label: 'Total Revenue', value: `CA$${(stats?.total_revenue ?? 0).toFixed(2)}`, icon: DollarSign, color: 'bg-pink-100 text-pink-600', link: '/admin/shop/orders' },
  ];

  const quickLinks = [
    { label: 'Manage Products', path: '/admin/shop/products', icon: Package, desc: 'Add, edit, delete products' },
    { label: 'Manage Categories', path: '/admin/shop/categories', icon: ShoppingBag, desc: 'Organize product categories' },
    { label: 'View Orders', path: '/admin/shop/orders', icon: ShoppingCart, desc: 'Process and track orders' },
    { label: 'Shop Settings', path: '/admin/shop/settings', icon: DollarSign, desc: 'Tax, shipping, currency' },
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <ShoppingBag className="w-8 h-8 text-pink-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shop Management</h1>
          <p className="text-gray-500 text-sm">Manage your e-commerce store</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {cards.map((card) => (
          <Link key={card.label} to={card.link} className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${card.color}`}>
              <card.icon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{loading ? '...' : card.value}</p>
            <p className="text-xs text-gray-500 mt-1">{card.label}</p>
          </Link>
        ))}
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {quickLinks.map((link) => (
          <Link key={link.path} to={link.path} className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
            <link.icon className="w-8 h-8 text-pink-500 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">{link.label}</h3>
            <p className="text-gray-500 text-sm">{link.desc}</p>
          </Link>
        ))}
      </div>

      {/* Recent Orders */}
      {stats?.recent_orders?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Recent Orders</h2>
            <Link to="/admin/shop/orders" className="text-pink-500 text-sm font-medium hover:underline">View All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-medium">Customer</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Total</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Status</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_orders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-50">
                    <td className="py-3 text-gray-900">{order.user_name || order.user_email}</td>
                    <td className="py-3 font-semibold text-gray-900">{order.currency_symbol || 'CA$'}{parseFloat(order.total).toFixed(2)}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {order.status === 'paid' ? 'Paid' : order.status === 'shipped' ? 'Shipped' : order.status === 'delivered' ? 'Delivered' : 'Pending'}
                      </span>
                    </td>
                    <td className="py-3 text-gray-500">{new Date(order.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShop;
